import { useAuthStore } from "@/store/auth";

/**
 * Non-2xx response carrying its parsed body. fetchWithAuth historically threw
 * a bare message and discarded the body; billing flows need the server's error
 * contract (e.g. 402 `{error: "payment_declined", code}`) to render recovery UI.
 */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, body: unknown) {
    super(`Request failed with status ${status}`);
    this.name = "ApiRequestError";
    this.status = status;
    this.body = body;
  }
}

/**
 * Fetches with the current access token and a configurable abort timeout.
 * Non-2xx responses preserve their parsed body only when requested.
 *
 * @param url - Resource URL.
 * @param options - Fetch options plus a 10-second-default timeout and error-body mode.
 * @returns Response decoded as JSON when declared, otherwise as text.
 * @throws ApiRequestError when `preserveErrorBody` is true for a non-2xx response.
 * @throws Error for other non-2xx responses; fetch and parsing failures propagate.
 */
export const fetchWithAuth = async <T>(
  url: string,
  options: RequestInit & {
    timeout?: number;
    /** Throw ApiRequestError (status + parsed body) instead of a bare Error. */
    preserveErrorBody?: boolean;
  } = {},
): Promise<T> => {
  const { timeout = 10_000, preserveErrorBody, ...fetchOptions } = options;

  try {
    // Clone headers to avoid modifying the original object
    const headers = new Headers(fetchOptions.headers || {});

    // Add authorization header if we have a token
    const token = await useAuthStore.getState().getAccessToken();
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    // Create an AbortController for timeout handling
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      // Make the request with the updated headers and timeout
      const response = await fetch(url, {
        ...fetchOptions,
        headers,
        signal: controller.signal,
      });

      // Handle errors
      if (!response.ok) {
        if (preserveErrorBody) {
          const body = await response.json().catch(() => null);
          throw new ApiRequestError(response.status, body);
        }
        throw new Error(`Request failed with status ${response.status}`);
      }

      // Parse response based on content type
      const contentType = response.headers.get("content-type");
      let data: T;
      if (contentType?.includes("application/json")) {
        data = await response.json();
      } else {
        data = (await response.text()) as unknown as T;
      }

      return data;
    } finally {
      clearTimeout(timeoutId);
    }
  } catch (error) {
    console.error(error);
    throw error;
  }
};
